import * as THREE from 'three';
import type { NightRig } from '@/fx/lighting';
import { fogRange } from '@/core/IsoCamera';

const SETTINGS_KEY = 'newamsterdam.visualSettings';

export const VISUAL_SETTINGS = {
  min: 0,
  max: 100,
  /**
   * Standaardstanden.
   *
   * De stad was zo donker dat je personages kwijtraakte. De schuif kon dat niet oplossen —
   * die schaalde rond een vaste basis en had vanaf 68 nog maar een kwart marge — dus de
   * standaard is opgetrokken en het bereik eromheen fors verruimd.
   */
  brightnessDefault: 72,
  ambientDefault: 65,
  characterLightDefault: 60,
  background: 0x07080a,
} as const;

/**
 * Grenswaarden van de drie schuiven.
 *
 * Elke schuif loopt van "donkerder dan de game ooit was" tot "ruim verlicht", zodat er in
 * beide richtingen echt iets te kiezen valt. Wie de oude noir-sfeer terug wil zet ze laag.
 */
const RANGE = {
  /** Belichting vóór de tonecurve. Raakt het hele beeld, inclusief neon en verlichte ramen. */
  exposureMin: 0.55,
  exposureMax: 3.2,
  /** Hemisphere-licht: bepaalt of onbelichte vlakken überhaupt zichtbaar zijn. */
  ambientMin: 0.35,
  ambientMax: 4.2,
  /** Maanlicht: geeft de massa's hun richting en schaduw. */
  moonMin: 0.35,
  moonMax: 2.4,
  /** Het lampje dat met de speler meereist. */
  characterMin: 0.6,
  characterMax: 5.5,
} as const;

export interface VisualSettingsData {
  readonly brightness: number;
  readonly ambient: number;
  readonly characterLight: number;
}

export interface VisualSettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Alleen wat we daadwerkelijk aanraken, niet de hele `WebGLRenderer`.
 *
 * Een echte renderer is in vitest niet te construeren — er is geen WebGL — en dit is het
 * enige veld dat deze module nodig heeft.
 */
export interface ExposureTarget {
  toneMappingExposure: number;
}

export interface VisualLightingTargets {
  readonly scene: THREE.Scene;
  readonly night: NightRig;
  readonly characterLight: THREE.PointLight;
  readonly renderer?: ExposureTarget;
}

function clamp(value: number): number {
  return THREE.MathUtils.clamp(value, VISUAL_SETTINGS.min, VISUAL_SETTINGS.max);
}

function defaultStorage(): VisualSettingsStorage | null {
  try {
    return globalThis.localStorage as VisualSettingsStorage | undefined ?? null;
  } catch {
    return null;
  }
}

export class VisualSettings {
  #brightness: number = VISUAL_SETTINGS.brightnessDefault;
  #ambient: number = VISUAL_SETTINGS.ambientDefault;
  #characterLight: number = VISUAL_SETTINGS.characterLightDefault;

  constructor(private readonly storage: VisualSettingsStorage | null = defaultStorage()) {
    this.load();
  }

  get brightness(): number {
    return this.#brightness;
  }

  get ambient(): number {
    return this.#ambient;
  }

  get characterLight(): number {
    return this.#characterLight;
  }

  setBrightness(value: number): void {
    this.#set('brightness', value);
  }

  setAmbient(value: number): void {
    this.#set('ambient', value);
  }

  setCharacterLight(value: number): void {
    this.#set('characterLight', value);
  }

  /** Alles terug naar de standaardstand — de ontsnapping als je jezelf hebt vastgedraaid. */
  reset(): void {
    this.#brightness = VISUAL_SETTINGS.brightnessDefault;
    this.#ambient = VISUAL_SETTINGS.ambientDefault;
    this.#characterLight = VISUAL_SETTINGS.characterLightDefault;
    this.save();
  }

  /**
   * Een save van vóór het instellingenpaneel bevat alleen `brightness`. De ontbrekende velden
   * moeten dan op hun standaard blijven staan in plaats van als `undefined` door te lekken en
   * de hele verlichting op `NaN` te zetten.
   */
  load(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.getItem(SETTINGS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<VisualSettingsData>;
      if (typeof parsed.brightness === 'number') this.#brightness = clamp(parsed.brightness);
      if (typeof parsed.ambient === 'number') this.#ambient = clamp(parsed.ambient);
      if (typeof parsed.characterLight === 'number') this.#characterLight = clamp(parsed.characterLight);
    } catch {
      this.reset();
    }
  }

  save(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(SETTINGS_KEY, JSON.stringify(this.toJSON()));
    } catch {
      // Visual preferences are nice to keep, but never important enough to stop the game.
    }
  }

  toJSON(): VisualSettingsData {
    return {
      brightness: this.#brightness,
      ambient: this.#ambient,
      characterLight: this.#characterLight,
    };
  }

  apply(targets: VisualLightingTargets): void {
    applyVisualSettings(targets, this.toJSON());
  }

  #set(key: keyof VisualSettingsData, value: number): void {
    const next = clamp(value);
    if (key === 'brightness') {
      if (next === this.#brightness) return;
      this.#brightness = next;
    } else if (key === 'ambient') {
      if (next === this.#ambient) return;
      this.#ambient = next;
    } else {
      if (next === this.#characterLight) return;
      this.#characterLight = next;
    }
    this.save();
  }
}

/**
 * Zet de drie schuiven om naar echte renderwaarden.
 *
 * De helderheidsschuif stuurt vooral `toneMappingExposure` aan, en dat is bewust: dat is de
 * enige knop die het héle beeld raakt, inclusief de emissieve ramen en het neon die volgens
 * `docs/ART_DIRECTION.md` het meeste visuele werk doen. Alleen lichtbronnen optrekken laat
 * die accenten juist achter, waardoor het beeld wel lichter wordt maar niet leesbaarder.
 *
 * @param settings Losse getallen mogen ook; ontbrekende velden vallen terug op hun standaard,
 *                 zodat oudere aanroepers met alleen een helderheid blijven werken.
 */
export function applyVisualSettings(
  targets: VisualLightingTargets,
  settings: number | Partial<VisualSettingsData>,
): void {
  const values = typeof settings === 'number' ? { brightness: settings } : settings;
  const brightness = clamp(values.brightness ?? VISUAL_SETTINGS.brightnessDefault) / 100;
  const ambient = clamp(values.ambient ?? VISUAL_SETTINGS.ambientDefault) / 100;
  const character = clamp(values.characterLight ?? VISUAL_SETTINGS.characterLightDefault) / 100;
  const lift = brightness - 0.5;

  if (targets.renderer) {
    targets.renderer.toneMappingExposure = THREE.MathUtils.lerp(RANGE.exposureMin, RANGE.exposureMax, brightness);
  }

  // Het omgevingslicht schaalt mee met de helderheid maar houdt zijn eigen schuif: hoeveel
  // schaduwdetail je wilt is een andere vraag dan hoe licht het beeld in totaal is.
  targets.night.ambient.intensity = THREE.MathUtils.lerp(RANGE.ambientMin, RANGE.ambientMax, ambient)
    * THREE.MathUtils.lerp(0.7, 1.35, brightness);
  targets.night.moon.intensity = THREE.MathUtils.lerp(RANGE.moonMin, RANGE.moonMax, brightness);
  targets.characterLight.intensity = THREE.MathUtils.lerp(RANGE.characterMin, RANGE.characterMax, character);

  const background = new THREE.Color(VISUAL_SETTINGS.background).lerp(
    new THREE.Color(0x1d2126),
    THREE.MathUtils.clamp(lift * 1.65, 0, 1),
  );
  targets.scene.background = background;

  if (targets.scene.fog instanceof THREE.Fog) {
    const baseFog = fogRange();
    targets.scene.fog.color.copy(background);
    targets.scene.fog.near = baseFog.near + lift * 10;
    targets.scene.fog.far = baseFog.far + lift * 28;
  }
}
